import { useMemo } from 'react';

import { A2uiOperation } from '~/components/AIInsightsModal/AIInsightsModal.utils';
import {
  findGeoFilterPaths,
  PulseFormBindings,
  PulseSelection,
  sanitizePulseFilters,
} from '~/components/AIInsightsUI/a2ui/PeopleCount';
import { useAgentPulseAudienceSelectionQuery } from '~/types/api.graphql';

import { CardField } from './confirmationFields';
import { isStringArray } from './confirmationSurface';
import { MutationConfirmation } from './mutationConfirmation.types';

interface CardPulseSelectionArgs {
  confirmation: MutationConfirmation;
  fields: CardField[];
  operations: A2uiOperation[] | null;
  /** False on answered or expired cards: the audience's filters are read from the cache only. */
  active: boolean;
}

interface CardPulseSelection {
  /** Null when the card isn't a Pulse one, or the audience's current filters couldn't be loaded. */
  bindings: PulseFormBindings | null;
  /** The proposed selection; null while the audience's current filters load. */
  baseline: PulseSelection | null;
}

/**
 * The Pulse selection a confirmation card proposes, found by its display hints: the topic ids of the
 * `pulseTopics` field and the person filters of the `filters` field. An edit of an existing audience
 * leaves out what it doesn't change (topics, filters or both), which keeps its current value, so the
 * audience's current topics and filters are loaded and counted for whatever the card doesn't propose.
 */
export const useCardPulseSelection = ({
  confirmation,
  fields,
  operations,
  active,
}: CardPulseSelectionArgs): CardPulseSelection => {
  // The classification marks a Pulse card even when its topics field wasn't proposed.
  const isPulseCard = Object.values(confirmation.fields ?? {}).some((field) => field.display === 'pulseTopics');
  const topicsField = fields.find((field) => field.display === 'pulseTopics');
  const filtersField = fields.find((field) => field.display === 'filters');
  // A Pulse mutation targeting an existing audience (an edit) carries its id; a create doesn't.
  const audienceId = isPulseCard && (!topicsField || !filtersField) ? readAudienceId(confirmation.values) : null;

  const { data, error } = useAgentPulseAudienceSelectionQuery({
    variables: { audienceId: audienceId ?? '' },
    skip: !audienceId,
    fetchPolicy: active ? 'cache-first' : 'cache-only',
  });

  const topicsPath = topicsField ? `/${topicsField.name}` : null;
  const bindings = useMemo(() => {
    if (!isPulseCard || error || (!topicsPath && !audienceId)) return null;
    return { topicsPath, geoFilterPaths: operations ? findGeoFilterPaths(operations) : [] };
  }, [isPulseCard, error, topicsPath, audienceId, operations]);

  const baseline = useMemo(() => {
    if (audienceId && !data) return null;
    const info = data?.audience?.info;
    const storedTopicIds = info && 'topicIds' in info ? info.topicIds : [];
    return {
      topicIds: topicsField ? (isStringArray(topicsField.value) ? topicsField.value : []) : storedTopicIds,
      filters: sanitizePulseFilters(filtersField ? filtersField.value : data?.audience?.filters),
    };
  }, [audienceId, data, topicsField, filtersField]);

  return { bindings, baseline };
};

const readAudienceId = (values: Record<string, unknown> | undefined): string | null => {
  const id = values?.id;
  return typeof id === 'string' || typeof id === 'number' ? String(id) : null;
};
